import { apiClient } from '../api'

/** Cached health probe payload (the latest run's summary for freshdesk). */
export interface FreshdeskHealth {
  ok?: boolean
  domain?: string
  checked_at?: string
  latency_ms?: number
  error?: string | null
}

/** Live in-flight progress for a running run (ignored once finished). */
export interface IntegrationRunProgress {
  note?: string
  current?: number | null
  total?: number | null
  at?: string
}

/** One `integration_runs` row as surfaced to the UI. */
export interface IntegrationRun {
  id: number
  integration_key: string
  trigger: string
  initiated_by: string | null
  status: string
  started_at: string
  finished_at: string | null
  duration_seconds: number | null
  summary: Record<string, unknown> | null
  // Optional: always present from the API, but optional here so run-shaped test
  // fixtures and any local constructors need not set it.
  progress?: IntegrationRunProgress | null
  error: string | null
}

/** Landing-card metadata for one integration. */
export interface IntegrationCard {
  key: string
  label: string
  description: string
  wired: boolean
  enabled: boolean
  last_run: IntegrationRun | null
}

/** Detail-page status: card fields + per-integration enrichment. */
export interface IntegrationStatus extends IntegrationCard {
  domain: string | null
  health: FreshdeskHealth | null
}

export interface TriggerRunResponse {
  run_id: number
  status: string
}

/** One synced Solutions article (Solutions tab + exception-link picker). */
export interface FreshdeskSolutionArticle {
  article_id: string
  market: 'ANZ' | 'USA'
  title: string
  url: string
  folder: string | null
  folder_id: number | null
  category: string | null
  category_id: number | null
  status: number // 1 = draft, 2 = published
}

/** One synced Solutions category (with rolled-up child counts). */
export interface FreshdeskSolutionCategory {
  category_id: number
  market: 'ANZ' | 'USA'
  name: string
  visible_in_portals: number[] | null
  folder_count: number
  article_count: number
}

/** One synced Solutions folder (with parent category name + article count). */
export interface FreshdeskSolutionFolder {
  folder_id: number
  category_id: number | null
  category: string | null
  market: 'ANZ' | 'USA'
  name: string
  article_count: number
}

/** The whole Solutions tree for the Solutions tab (one fetch). */
export interface FreshdeskSolutions {
  categories: FreshdeskSolutionCategory[]
  folders: FreshdeskSolutionFolder[]
  articles: FreshdeskSolutionArticle[]
}

/** One synced Freshdesk company, matched to an FP by name (Companies tab). */
export interface FreshdeskCompany {
  id: number
  name: string
  sub_code: string | null
  market: 'ANZ' | 'USA' | null
  match_method: 'name' | 'unmatched'
  fp_code: string | null
  franchise_name: string | null
}

/** All synced companies for the Companies tab (one fetch). */
export interface FreshdeskCompanies {
  companies: FreshdeskCompany[]
}

/** One data type's row count within a section. */
export interface FreshdeskEntityCount {
  key: string
  label: string
  count: number
}

/** One Freshdesk section (Solutions / Contacts / Tickets) for the Status panel. */
export interface FreshdeskSectionSummary {
  key: string
  label: string
  tab: string // route segment under /integrations/freshdesk ('' = Status)
  available: boolean
  last_synced_at: string | null
  entities: FreshdeskEntityCount[]
}

export const integrationsApi = {
  listIntegrations: async (): Promise<IntegrationCard[]> => {
    const response = await apiClient.get('/admin/integrations')
    return response.data.items
  },

  getIntegrationStatus: async (key: string): Promise<IntegrationStatus> => {
    const response = await apiClient.get(`/admin/integrations/${key}/status`)
    return response.data
  },

  listIntegrationRuns: async (
    key: string,
    limit = 20,
  ): Promise<IntegrationRun[]> => {
    const response = await apiClient.get(
      `/admin/integrations/${key}/runs?limit=${limit}`,
    )
    return response.data.items
  },

  triggerIntegrationRun: async (key: string): Promise<TriggerRunResponse> => {
    const response = await apiClient.post(`/admin/integrations/${key}/run`)
    return response.data
  },

  /** Synced Solutions articles (DB read). Powers the exception-link picker. */
  getFreshdeskSolutionArticles: async (
    market?: 'ANZ' | 'USA',
  ): Promise<FreshdeskSolutionArticle[]> => {
    const query = market ? `?market=${market}` : ''
    const response = await apiClient.get(
      `/admin/freshdesk/solutions/articles${query}`,
    )
    return response.data.items
  },

  /** The whole Solutions tree (categories + folders + articles) - Solutions tab. */
  getFreshdeskSolutions: async (): Promise<FreshdeskSolutions> => {
    const response = await apiClient.get('/admin/freshdesk/solutions')
    return response.data
  },

  /** Synced Freshdesk companies matched to FPs by name - Companies tab. */
  getFreshdeskCompanies: async (): Promise<FreshdeskCompanies> => {
    const response = await apiClient.get('/admin/freshdesk/companies')
    return response.data
  },

  /** Per-section counts + freshness for the Status page Data panel. */
  getFreshdeskSummary: async (): Promise<FreshdeskSectionSummary[]> => {
    const response = await apiClient.get('/admin/freshdesk/summary')
    return response.data.items
  },

  /** Recent runs across ALL Freshdesk jobs (health + Solutions + future syncs). */
  getFreshdeskRuns: async (limit = 20): Promise<IntegrationRun[]> => {
    const response = await apiClient.get(`/admin/freshdesk/runs?limit=${limit}`)
    return response.data.items
  },
}
